import {
  createContext,
  useMemo,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLocalStorage } from './useLocalStorage';
import type { ReactNode } from 'react';
import api from '@/services/api';
import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import Suspense from '@/components/Suspense';

export interface AuthContextType {
  user: User | null;
  login: (data: User) => Promise<void>;
  logout: () => void;
  loading: boolean;
}

interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useLocalStorage<User | null>('user', null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const handleLogout = useCallback(() => {
    setUser(null);
    navigate('/', { replace: true });
  }, [setUser, navigate]);

  // interceptor
  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && error.response.status === 401) {
          enqueueSnackbar(t('login.tokenWarning'), {
            variant: 'warning',
          });
          handleLogout();
        }
        return Promise.reject(error);
      }
    );
    return () => api.interceptors.response.eject(interceptor);
  }, [handleLogout]);

  useEffect(() => {
    /** only do the request if we have a logged user in the storage and 
    if the route is not the public login route (to avoid loop) **/
    if (user && location.pathname !== '/') {
      const verifyToken = async () => {
        try {
          const response = await api.get('/auth/status');

          if (response.status === 200) {
            setUser(response.data);
          }
        } catch {
          // if 401 Unauthorized, the incerpertor will handle the logout and redirection
          // so we just log here for debugging.
          enqueueSnackbar(t('login.tokeWarningValidation'), {
            variant: 'warning',
          });
        } finally {
          setLoading(false);
        }
      };

      verifyToken();
    } else {
      setLoading(false);
    }
  }, [location.pathname]);

  const login = useCallback(
    async (data: User) => {
      setUser(data);
      await new Promise((resolve) => setTimeout(resolve, 100)); // Pequeno delay
      //console.log('Estado do usuário definido no AuthProvider. User:', user); // Verifique o estado atualizado
    },
    [setUser]
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout').then((response) => {
        if (response.status === 200) {
          setUser(null);
          navigate('/', { replace: true });
        }
      });
    } catch (error) {
      enqueueSnackbar(t('logoutError' + error), { variant: 'error' });
    }
  }, [setUser, navigate]);

  const value = useMemo(
    () => ({ user, login, logout, loading }),
    [user, login, logout, loading]
  );

  return (
    <AuthContext.Provider value={value}>
      {loading ? <Suspense>{children}</Suspense> : children}
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthProvider };
